import { useCallback, useRef, useState } from "react";
import {
  canDragSession,
  canRescheduleSessionTo,
  rescheduleBlockedMessage,
  sessionDragBlockedMessage,
} from "./calendarUtils";

function dropTargetKey(target) {
  if (!target) return "";
  return `${target.day || ""}:${target.hour}`;
}

/**
 * Общий DnD для дневной и недельной сетки: без лишних setState на dragOver,
 * без мерцания drop-зоны из-за dragLeave на дочерних элементах.
 */
export function useSessionGridDrag({ sessions, onReschedule, onBlocked }) {
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropHint, setDropHint] = useState("");
  const dragMoved = useRef(false);
  const dropKeyRef = useRef("");
  const hintKeyRef = useRef("");

  const draggingSession = sessions.find((s) => s.id === draggingId) || null;

  const notifyBlocked = useCallback(
    (message) => {
      if (message) onBlocked?.(message);
    },
    [onBlocked],
  );

  const setDrop = useCallback((target) => {
    const key = dropTargetKey(target);
    if (key === dropKeyRef.current) return;
    dropKeyRef.current = key;
    setDropTarget(target);
  }, []);

  const setHint = useCallback((message) => {
    const text = message || "";
    if (text === hintKeyRef.current) return;
    hintKeyRef.current = text;
    setDropHint(text);
  }, []);

  const clearDrag = useCallback(() => {
    setDraggingId(null);
    dropKeyRef.current = "";
    hintKeyRef.current = "";
    setDropTarget(null);
    setDropHint("");
  }, []);

  const explainBlockedDrag = useCallback(
    (sess) => {
      notifyBlocked(sessionDragBlockedMessage(sess));
    },
    [notifyBlocked],
  );

  const startDrag = useCallback(
    (e, sess) => {
      if (!canDragSession(sess)) {
        e.preventDefault();
        explainBlockedDrag(sess);
        return;
      }
      dragMoved.current = false;
      setHint("");
      e.dataTransfer.setData("text/session-id", sess.id);
      e.dataTransfer.effectAllowed = "move";
      const ghost = document.createElement("div");
      ghost.style.cssText =
        "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0";
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 0, 0);
      requestAnimationFrame(() => ghost.remove());
      setDraggingId(sess.id);
    },
    [explainBlockedDrag, setHint],
  );

  const onDrag = useCallback((e) => {
    if (e.clientX !== 0 || e.clientY !== 0) {
      dragMoved.current = true;
    }
  }, []);

  const onDragEnd = useCallback(() => {
    clearDrag();
  }, [clearDrag]);

  const overCell = useCallback(
    (e, cell) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggingSession) return;
      const check = canRescheduleSessionTo(
        draggingSession,
        cell.day,
        cell.hour,
      );
      if (check.ok) {
        e.dataTransfer.dropEffect = "move";
        setDrop(cell);
        setHint("");
      } else {
        e.dataTransfer.dropEffect = "none";
        setDrop(null);
        setHint(rescheduleBlockedMessage(check.reason) || "");
      }
    },
    [draggingSession, setDrop, setHint],
  );

  const dropOnCell = useCallback(
    async (e, cell) => {
      e.preventDefault();
      e.stopPropagation();
      const id =
        e.dataTransfer.getData("text/session-id") || draggingId || "";
      const sess = sessions.find((x) => x.id === id);
      const check = sess
        ? canRescheduleSessionTo(sess, cell.day, cell.hour)
        : { ok: false };
      clearDrag();
      if (!sess) return;
      if (!check.ok) {
        notifyBlocked(
          rescheduleBlockedMessage(check.reason) ||
            "Нельзя перенести на это время",
        );
        return;
      }
      dragMoved.current = false;
      await onReschedule(sess, check.patch);
    },
    [sessions, draggingId, clearDrag, onReschedule, notifyBlocked],
  );

  const onClickCapture = useCallback((e) => {
    if (dragMoved.current) {
      dragMoved.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const isDropTarget = useCallback(
    (cell) =>
      Boolean(
        draggingId &&
          dropTarget &&
          dropTarget.day === cell.day &&
          dropTarget.hour === cell.hour,
      ),
    [draggingId, dropTarget],
  );

  const cardTitle = useCallback(
    (sess) => {
      if (canDragSession(sess)) {
        return "Перетащите на другой час до начала занятия";
      }
      return sessionDragBlockedMessage(sess) || "";
    },
    [],
  );

  return {
    draggingId,
    dropTarget,
    dropHint,
    startDrag,
    explainBlockedDrag,
    onDrag,
    onDragEnd,
    overCell,
    dropOnCell,
    onClickCapture,
    isDropTarget,
    canDragSession,
    cardTitle,
  };
}
